/// <reference types="spotify-web-playback-sdk" />
/// <reference types="spotify-api" />
import { AsyncEvent, chunked } from "../util";
import { raiseFromStatus } from "./errors";

interface SpotifyPlayerOptions {
  name: string;
  volume?: number;
}

interface InitOptions {
  getToken: () => string | Promise<string>;
}

interface RequestOptions {
  includeToken?: boolean;
  params?: { [key: string]: any };
  json?: any;
  body?: any;
  headers?: { [key: string]: string };
}

export class SpotifyClient {
  loaded = false;
  private getToken: () => string | Promise<string> = () => "";

  public async init(options: InitOptions): Promise<void> {
    if (this.loaded) {
      return;
    }
    const loadedEvent = new AsyncEvent();
    this.getToken = options.getToken;
    window.onSpotifyWebPlaybackSDKReady = async () => {
      console.info("Spotify SDK ready");
      loadedEvent.trigger();
      this.loaded = true;
    };
    this.injectScript();
    await loadedEvent.wait();
  }

  createPlayer(options: SpotifyPlayerOptions): Spotify.Player {
    const getOAuthToken = async (cb: (token: string) => void) => {
      const token = await this.getToken();
      cb(token);
    };
    const player = new Spotify.Player({
      getOAuthToken,
      ...options,
    });

    player.addListener("ready", (device) => {
      console.info("Spotify player ready", device);
    });

    player.addListener(
      "player_state_changed",
      (state: Spotify.PlaybackState | null) => {
        console.info("Spotify player state changed", state);
      }
    );

    player.addListener("not_ready", (msg) => {
      console.warn("Spotify player not ready", msg);
    });

    player.addListener("initialization_error", (error) => {
      console.warn("Spotify initialization error", error);
    });

    player.addListener("authentication_error", (error) => {
      console.warn("Spotify authentication error", error);
    });

    player.addListener("account_error", (error) => {
      console.warn("Spotify account error", error);
    });

    player.addListener("playback_error", (error) => {
      console.warn("Spotify account error", error);
    });
    return player;
  }

  private injectScript() {
    const s = document.createElement("script");
    s.src = "https://sdk.scdn.co/spotify-player.js";
    document.head.insertBefore(s, document.head.firstElementChild);
  }

  async apiRequest(
    method: string,
    url: string,
    options: RequestOptions = {}
  ): Promise<Response> {
    const urlObj = new URL("https://api.spotify.com/v1" + url);
    const fetchOptions: any = { method: method, headers: {} };
    if (options.json) {
      fetchOptions.body = JSON.stringify(options.json);
      fetchOptions.headers["Content-Type"] = "application/json";
    } else if (options.body) {
      fetchOptions.body = options.body;
    }
    if (options.params) {
      for (const [key, value] of Object.entries(options.params)) {
        urlObj.searchParams.append(key, value);
      }
    }
    fetchOptions.headers.Authorization = "Bearer " + (await this.getToken());
    fetchOptions.headers = {
      ...fetchOptions.headers,
      ...(options.headers || {}),
    };

    const resp = await fetch(urlObj.toString(), fetchOptions);
    return await raiseFromStatus(resp);
  }

  async getSeveralTracks(
    trackIds: string[]
  ): Promise<SpotifyApi.TrackObjectFull[]> {
    const result: SpotifyApi.TrackObjectFull[] = [];
    // Load track in chunks of 50
    for (const chunk of chunked(trackIds, 50)) {
      const resp = await this.apiRequest("GET", "/tracks", {
        params: { ids: chunk.join(",") },
      });
      const data = await resp.json();
      result.push(...data.tracks);
    }
    return result;
  }

  private async play(
    context_uri: string,
    options: { position?: number; deviceId?: string } = {}
  ) {
    const opts: Record<string, any> = {};
    if (options.position) {
      opts["offset"] = { position: options.position };
    }
    const params: Record<string, any> = {};
    if (options.deviceId) {
      params.device_id = options.deviceId;
    }
    await this.apiRequest("PUT", "/me/player/play", {
      params,
      json: {
        context_uri,
        ...opts,
      },
    });
  }

  public async playPlaylist(
    playlistId: string,
    options: { position?: number; deviceId?: string } = {}
  ): Promise<void> {
    this.play(`spotify:playlist:${playlistId}`, options);
  }

  public async playAlbum(
    albumId: string,
    options: { position?: number; deviceId?: string } = {}
  ): Promise<void> {
    this.play(`spotify:album:${albumId}`, options);
  }

  public async playTrack(
    trackId: string,
    options: { positionMs?: number; deviceId?: string } = {}
  ): Promise<void> {
    const opts: Record<string, any> = {};
    if (options.positionMs) {
      opts["position_ms"] = options.positionMs;
    }
    const params: Record<string, any> = {};
    if (options.deviceId) {
      params.device_id = options.deviceId;
    }
    await this.apiRequest("PUT", "/me/player/play", {
      params,
      json: {
        uris: [`spotify:track:${trackId}`],
        ...opts,
      },
    });
  }

  public async playTracks(
    trackIds: string[],
    options: { position?: number; deviceId?: string } = {}
  ): Promise<void> {
    const opts: Record<string, any> = {};
    if (options.position) {
      opts["offset"] = { position: options.position };
    }
    const params: Record<string, any> = {};
    if (options.deviceId) {
      params.device_id = options.deviceId;
    }
    await this.apiRequest("PUT", "/me/player/play", {
      params,
      json: {
        uris: trackIds.map((id) => `spotify:track:${id}`),
        ...opts,
      },
    });
  }

  public async getDevices(): Promise<SpotifyApi.UserDevicesResponse> {
    const resp = await this.apiRequest("GET", "/me/player/devices");
    const data: SpotifyApi.UserDevicesResponse = await resp.json();
    return data;
  }

  public async previousTrack() {
    await this.apiRequest("POST", "/me/player/previous");
  }

  public async nextTrack() {
    await this.apiRequest("POST", "/me/player/next");
  }

  async pause(deviceId?: string): Promise<void> {
    const params: Record<string, any> = {};
    if (deviceId) {
      params.device_id = deviceId;
    }
    await this.apiRequest("PUT", "/me/player/pause", { params });
  }

  async resume(deviceId?: string): Promise<void> {
    const params: Record<string, any> = {};
    if (deviceId) {
      params.device_id = deviceId;
    }
    await this.apiRequest("PUT", "/me/player/play", { params });
  }

  /**
   * Set the volume of the player
   * @param volume Volume between 0 and 1
   */
  async setVolume(volume: number): Promise<void> {
    const volumePercent = Math.floor(volume * 100);
    await this.apiRequest(
      "PUT",
      `/me/player/volume?volume_percent=${volumePercent}`
    );
  }

  public async togglePlayback(shuffle: boolean) {
    await this.apiRequest(
      "PUT",
      `/me/player/shuffle?state=${shuffle ? "true" : "false"}`
    );
  }

  async setRepeatMode(
    mode: "track" | "context" | "off",
    deviceId?: string
  ): Promise<void> {
    const params: Record<string, any> = { state: mode };
    if (deviceId) {
      params.device_id = deviceId;
    }

    await this.apiRequest("PUT", "/me/player/repeat", { params });
  }

  public async transferPlayback(deviceId: string) {
    await this.apiRequest("PUT", "/me/player", {
      json: {
        device_ids: [deviceId],
        play: true,
      },
    });
  }
}

export default new SpotifyClient();
